import React, { Fragment, useState, useEffect } from 'react';
import Breadcrumb from '../common/breadcrumb';
import notie from 'notie';
import '../../assets/css/notie.css';
import Loader from '../common/loaderapp';
import Tablereact from '../common/tabulator/tablereact';
import InlineLabelsChart from './InlineLabelsChart';

const Estadisticas = () => {
    const [datos, setDatos] = useState([]);
    const [datosParaGrafica, setDatosParaGrafica] = useState([]);
    const [loading, setLoading] = useState(true);
    const [carritosPendientes, setCarritosPendientes] = useState(0);
    const [loadingCarritos, setLoadingCarritos] = useState(true);
    const [carritosCompletados, setCarritosCompletados] = useState(0);
    const [loadingCarritosCompletados, setLoadingCarritosCompletados] = useState(true);
    const [productosMasCotizados, setProductosMasCotizados] = useState([]);
    const [loadingProductos, setLoadingProductos] = useState(true);
    const [totalClientes, setTotalClientes] = useState(0);
    const [loadingClientesTotal, setLoadingClientesTotal] = useState(true);
    const [clientesMasCotizaciones, setClientesMasCotizaciones] = useState([]);
    const [loadingClientes, setLoadingClientes] = useState(true);
    const [filterType, setFilterType] = useState('last7');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const toDateString = (date) => date.toISOString().slice(0, 10);

    const formatShortDate = (dateString) => {
        if (!dateString) return '--/--/--';
        const parsed = new Date(dateString);
        if (Number.isNaN(parsed.getTime())) return '--/--/--';
        const dd = String(parsed.getDate()).padStart(2, '0');
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const yy = String(parsed.getFullYear()).slice(-2);
        return `${dd}/${mm}/${yy}`;
    };

    const getRangeForFilter = (type) => {
        const today = new Date();
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        let start = new Date(end);

        switch (type) {
            case 'today':
                start = new Date(end);
                break;
            case 'last30':
                start.setDate(start.getDate() - 29);
                break;
            case 'lastMonth': {
                const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
                const lastOfPrevMonth = new Date(firstOfThisMonth);
                lastOfPrevMonth.setDate(0);
                start = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
                return { start: toDateString(start), end: toDateString(lastOfPrevMonth) };
            }
            case 'lastYear':
                start.setDate(start.getDate() - 364);
                break;
            case 'custom':
                return { start: startDate, end: endDate };
            case 'last7':
            default:
                start.setDate(start.getDate() - 6);
                break;
        }

        return { start: toDateString(start), end: toDateString(end) };
    };

    useEffect(() => {
        const initial = getRangeForFilter('last7');
        setStartDate(initial.start);
        setEndDate(initial.end);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const cargarEstadisticas = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/p_estadisticas?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const lista = Array.isArray(payload) ? payload : [];

                const datosGrafica = [];
                const datosTabla = [];

                lista.forEach((row) => {
                    const rawDate = row.fecha || row.FECHA || row.date;
                    const parsedDate = rawDate ? new Date(rawDate) : null;
                    const total = Number(row.total ?? row.TOTAL ?? row.value ?? 0);

                    if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
                        datosGrafica.push({
                            date: parsedDate,
                            value: Number.isFinite(total) ? total : 0
                        });
                        datosTabla.push({
                            fecha: parsedDate.toISOString().slice(0, 10),
                            total: Number.isFinite(total) ? total : 0
                        });
                    }
                });

                if (isMounted) {
                    setDatosParaGrafica(datosGrafica);
                    setDatos(datosTabla);
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar las estadisticas.', time: 3 });
                if (isMounted) {
                    setDatosParaGrafica([]);
                    setDatos([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        if (startDate && endDate) {
            cargarEstadisticas();
        }

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate]);

    useEffect(() => {
        let isMounted = true;

        const cargarTotalClientes = async () => {
            try {
                setLoadingClientesTotal(true);
                const response = await fetch('/p_estadisticas_clientes_total', {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const total = Number(payload?.total ?? 0);
                if (isMounted) {
                    setTotalClientes(Number.isFinite(total) ? total : 0);
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar los clientes.', time: 3 });
                if (isMounted) {
                    setTotalClientes(0);
                }
            } finally {
                if (isMounted) {
                    setLoadingClientesTotal(false);
                }
            }
        };

        cargarTotalClientes();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const cargarClientesMasCotizaciones = async () => {
            try {
                setLoadingClientes(true);
                const response = await fetch(`/p_estadisticas_clientes?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const lista = Array.isArray(payload) ? payload : [];
                if (isMounted) {
                    setClientesMasCotizaciones(lista.map(item => ({
                        nombre: [item.nombre ?? item.NOMBRE ?? '', item.apellidos ?? item.APELLIDOS ?? ''].join(' ').trim(),
                        total: Number(item.total ?? item.TOTAL ?? 0)
                    })));
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar los clientes con mas cotizaciones.', time: 3 });
                if (isMounted) {
                    setClientesMasCotizaciones([]);
                }
            } finally {
                if (isMounted) {
                    setLoadingClientes(false);
                }
            }
        };

        if (startDate && endDate) {
            cargarClientesMasCotizaciones();
        }

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate]);

    useEffect(() => {
        let isMounted = true;

        const cargarCarritosPendientes = async () => {
            try {
                setLoadingCarritos(true);
                const response = await fetch(`/p_estadisticas_carritos?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const total = Number(payload?.total ?? 0);
                if (isMounted) {
                    setCarritosPendientes(Number.isFinite(total) ? total : 0);
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar los carritos pendientes.', time: 3 });
                if (isMounted) {
                    setCarritosPendientes(0);
                }
            } finally {
                if (isMounted) {
                    setLoadingCarritos(false);
                }
            }
        };

        if (startDate && endDate) {
            cargarCarritosPendientes();
        }

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate]);

    useEffect(() => {
        let isMounted = true;

        const cargarCarritosCompletados = async () => {
            try {
                setLoadingCarritosCompletados(true);
                const response = await fetch(`/p_estadisticas_carritos_completados?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const total = Number(payload?.total ?? 0);
                if (isMounted) {
                    setCarritosCompletados(Number.isFinite(total) ? total : 0);
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar los carritos completados.', time: 3 });
                if (isMounted) {
                    setCarritosCompletados(0);
                }
            } finally {
                if (isMounted) {
                    setLoadingCarritosCompletados(false);
                }
            }
        };

        if (startDate && endDate) {
            cargarCarritosCompletados();
        }

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate]);

    useEffect(() => {
        let isMounted = true;

        const cargarProductosMasCotizados = async () => {
            try {
                setLoadingProductos(true);
                const response = await fetch(`/p_estadisticas_productos?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });
                const payload = await response.json();
                const lista = Array.isArray(payload) ? payload : [];
                if (isMounted) {
                    setProductosMasCotizados(lista.map(item => ({
                        sku: item.sku ?? item.SKU ?? '',
                        cantidad: Number(item.cantidad ?? item.CANTIDAD ?? 0),
                        nombre: item.nombre ?? item.NOMBRE ?? ''
                    })));
                }
            } catch (error) {
                console.log('Error:', error);
                notie.alert({ type: 'error', text: 'No se pudieron cargar los productos mas cotizados.', time: 3 });
                if (isMounted) {
                    setProductosMasCotizados([]);
                }
            } finally {
                if (isMounted) {
                    setLoadingProductos(false);
                }
            }
        };

        if (startDate && endDate) {
            cargarProductosMasCotizados();
        }

        return () => {
            isMounted = false;
        };
    }, [startDate, endDate]);

    const cabecera = [
        { Header: <b>Fecha</b>, accessor: 'fecha', show: true },
        { Header: <b>Total</b>, accessor: 'total', show: true }
    ];

    const productos = [
        { Header: <b>Sku</b>, accessor: 'sku', show: true },
        { Header: <b>Cantidad</b>, accessor: 'cantidad', show: true },
        { Header: <b>Nombre</b>, accessor: 'nombre', show: true }
    ];

    const clientes = [
        { Header: <b>Nombre</b>, accessor: 'nombre', show: true },
        { Header: <b>Total de cotizaciones</b>, accessor: 'total', show: true }
    ];

    const detalle_dato = (dato) => {
        console.log(dato);
    };

    const handleFilterChange = (event) => {
        const value = event.target.value;
        setFilterType(value);
        const range = getRangeForFilter(value);
        if (range.start && range.end) {
            setStartDate(range.start);
            setEndDate(range.end);
        }
    };

    const handleStartDateChange = (event) => {
        setFilterType('custom');
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setFilterType('custom');
        setEndDate(event.target.value);
    };

    return (
        <Fragment>
            <Breadcrumb parent="Inicio" title="Estadisticas" />

                    <div className="col-12">
                        <div className="card">
                            <p className="my-4 mx-5">
                                Periodo de Informe del{' '}
                                <span style={{ fontWeight: '700', marginRight: '20px'}}>{formatShortDate(startDate)} al {formatShortDate(endDate)}</span>
                                <select
                                    value={filterType}
                                    onChange={handleFilterChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                >
                                    <option value="last7">Últimos 7 días</option>
                                    <option value="today">Hoy</option>
                                    <option value="last30">Últimos 30 días</option>
                                    <option value="lastMonth">Último Mes</option>
                                    <option value="lastYear">Último Año</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px'}}
                                />
                            </p>
                        </div>
                    </div>

            <div className="container-fluid">
                <div className="row">

                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-body">
                                {loading ? <Loader /> : <InlineLabelsChart data={datosParaGrafica} />}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="card justify-content-center text-center">
                            <div className="content py-5">
                                <h1 style={{fontWeight: '800', color: '#00003D'}}>{loadingCarritos ? '...' : carritosPendientes}</h1>
                                <h4 style={{fontWeight: '600', color: '#00003D'}}>CARRITOS PENDIENTES</h4>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="card justify-content-center text-center">
                            <div className="content py-5">
                                <h1 style={{fontWeight: '800', color: '#00003D'}}>{loadingCarritosCompletados ? '...' : carritosCompletados}</h1>
                                <h4 style={{fontWeight: '600', color: '#00003D'}}>CARRITOS COMPLETADOS</h4>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="card justify-content-center text-center">
                            <div className="content py-5">
                                <h1 style={{fontWeight: '800', color: '#00003D'}}>{loadingClientesTotal ? '...' : totalClientes}</h1>
                                <h4 style={{fontWeight: '600', color: '#00003D'}}>TOTAL DE CLIENTES</h4>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header d-flex align-items-center">
                                <h5 className="mr-5">Lista de datos</h5>
                                <select
                                    value={filterType}
                                    onChange={handleFilterChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                >
                                    <option value="last7">Últimos 7 días</option>
                                    <option value="today">Hoy</option>
                                    <option value="last30">Últimos 30 días</option>
                                    <option value="lastMonth">Último Mes</option>
                                    <option value="lastYear">Último Año</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px'}}
                                />
                            </div>
                            <div className="card-body datatable-react">
                                <Tablereact
                                    myData={datos}
                                    cabecera={cabecera}
                                    pageSize={datos.length > 0 ? Math.min(datos.length, 5) : 5}
                                    paginacion={datos.length > 5}
                                    minRows={6}
                                    buscador="Buscar estadistica"
                                />
                            </div>
                        </div>
                        <div className="card">
                            <div className="card-header d-flex align-items-center">
                                <h5 className="mr-5">Clientes con mas cotizaciones</h5>
                                <select
                                    value={filterType}
                                    onChange={handleFilterChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                >
                                    <option value="last7">Últimos 7 días</option>
                                    <option value="today">Hoy</option>
                                    <option value="last30">Últimos 30 días</option>
                                    <option value="lastMonth">Último Mes</option>
                                    <option value="lastYear">Último Año</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px'}}
                                />
                            </div>
                            <div className="card-body datatable-react">
                                <Tablereact
                                    myData={clientesMasCotizaciones}
                                    cabecera={clientes}
                                    pageSize={clientesMasCotizaciones.length > 0 ? Math.min(clientesMasCotizaciones.length, 20) : 20}
                                    paginacion={clientesMasCotizaciones.length > 20}
                                    minRows={6}
                                    buscador="Buscar cliente"
                                />
                                {loadingClientes ? <Loader /> : null}
                            </div>
                        </div>
                    </div>
                    

                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header d-flex align-items-center">
                                <h5 className="mr-5">Productos mas cotizados</h5>
                                <select
                                    value={filterType}
                                    onChange={handleFilterChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                >
                                    <option value="last7">Últimos 7 días</option>
                                    <option value="today">Hoy</option>
                                    <option value="last30">Últimos 30 días</option>
                                    <option value="lastMonth">Último Mes</option>
                                    <option value="lastYear">Último Año</option>
                                    <option value="custom">Personalizado</option>
                                </select>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px', marginRight: '10px'}}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    style={{ border: '0px solid', backgroundColor: '#f3f3f3', padding: '10px 15px', borderRadius: '6px'}}
                                />
                            </div>
                            <div className="card-body datatable-react" style={{ paddingBottom: '45px !important'}}>
                                <Tablereact
                                    myData={productosMasCotizados}
                                    cabecera={productos}
                                    pageSize={productosMasCotizados.length > 0 ? Math.min(productosMasCotizados.length, 20) : 20}
                                    paginacion={productosMasCotizados.length > 20}
                                    minRows={20}
                                    buscador="Buscar producto"
                                />
                                {loadingProductos ? <Loader /> : null}
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </Fragment>
    );
};

export default Estadisticas;

/* Jaime de donde saque la grafica es esta pagina https://d3js.org/ y la grafica que use es esta https://observablehq.com/@d3/new-zealand-tourists-1921-2018 por si la quieres cambiar */
